/*
App.js
Adam Berger
July 2023 
*/

// Third party 
import React from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { history } from 'instantsearch.js/es/lib/routers';
import { useEffect, useMemo } from 'react';
import { InstantSearch } from 'react-instantsearch-hooks-web';
import { Route, Routes } from 'react-router-dom';

// first-party 
import * as Constants from 'components/Constants';
import { AuthenticationGuard } from "./components/authentication-guard";

// Configure routing
const routing = {
  router: history(),
  stateMapping: {
    stateToRoute(uiState) {
      const indexUiState = uiState[Constants.ALGOLIA_INDEX_NAME];
      return {
        query: indexUiState.query,
        page: indexUiState.page,
        filters: indexUiState.filters,
      };
    },
    routeToState(routeState) {
      return {
        [Constants.ALGOLIA_INDEX_NAME]: {
          query: routeState.query,
          page: routeState.page,
          filters: routeState.filters,
        },
      };
    },
  },
};

// application 'pages'
// import Home from './components/pages/Home';
import {
  Browse,
  CreateFromImage,
  CreateFromIngredients,
  CreateFromUrl,
  CreateMenu,
  Detail,
  Edit,
  Home,
  Info
} from './pages';

// theme
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import theme from "assets/theme";

// Create a BrowseWithSearch component that wraps Browse with InstantSearch
const BrowseWithSearch = () => {
  const loggingSearchClient = useMemo(() => {
    return {
      ...Constants.searchClient,
      search(requests) {
        return Constants.searchClient.search(requests);
      },
    };
  }, []);

  return (
    <InstantSearch  
      searchClient={loggingSearchClient}
      indexName={Constants.ALGOLIA_INDEX_NAME}
      routing={routing}
      stalledSearchDelay={1000}
      initialUiState={{
        [Constants.ALGOLIA_INDEX_NAME]: {
          page: 0,
        },
      }}
    >
      <Browse />
    </InstantSearch>
  );
};

export const App = () => {
  const { isLoading } = useAuth0();
  // TODO: Use FidgetSpinner here.

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        // Scroll has occurred, hide the browser UI
        document.body.style.height = 'calc(100vh + 60px)';
      } else {
        // User is at the top, show the browser UI
        document.body.style.height = '100vh';
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);


  return (isLoading ? <div>Loading...</div> :
          <div className="fscontainer">
            <ThemeProvider theme={theme}>
              <CssBaseline/>
              <Routes>
                <Route path='/' element={<Home/>}/>
                <Route path='/info' element={<Info/>}/>
                <Route path='/browse' element={<AuthenticationGuard component={BrowseWithSearch}/>}/>
                <Route path='/createMenu' element={<CreateMenu/>}/>
                <Route path='/createFromUrl' element={<AuthenticationGuard component={CreateFromUrl}/>}/>
                <Route path='/createFromIngredients'
                       element={<AuthenticationGuard component={CreateFromIngredients}/>}/>
                <Route path='/createFromImage' element={<AuthenticationGuard component={CreateFromImage}/>}/>
                <Route path='/edit/:guid' element={<AuthenticationGuard component={Edit}/>}/>
                <Route path='/detail/:guid' element={<AuthenticationGuard component={Detail}/>}/>
                <Route path='/el/:guid' element={<AuthenticationGuard component={Detail}/>}/>
                <Route path="*" element={<Home/>}/>
              </Routes>
            </ThemeProvider>
          </div>
  )
}
